import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { SHADOW } from "../config/theme";
import ceLogoBlack from "../logos/CE_MonogramOnlyBlack.png";
import ceLogoWhite from "../logos/CE_MonogramOnlyWhite.png";

export interface NavigationProps {
  darkMode: boolean;
  onToggleDarkMode: () => void;
  onExportScreenshot: () => void;
  isExporting: boolean;
  exportError: string | null;
  onClearExportError: () => void;
  // Report history props (optional - only shown on results pages)
  showReportHistory?: boolean;
  reportHistoryCount?: number;
  onOpenReportHistory?: () => void;
  // View mode props (optional - only shown on results pages)
  viewMode?: "original" | "slide" | "compact";
  onViewModeChange?: (mode: "original" | "slide" | "compact") => void;
}

/**
 * Clean, modern navigation bar for the Campaign Reporting Dashboard.
 * Features brand identity, navigation links, and utility controls (dark mode, export).
 */
export const Navigation: React.FC<NavigationProps> = ({
  darkMode,
  onToggleDarkMode,
  onExportScreenshot,
  isExporting,
  exportError,
  onClearExportError,
  showReportHistory = false,
  reportHistoryCount = 0,
  onOpenReportHistory,
  viewMode,
  onViewModeChange,
}) => {
  const location = useLocation();
  const isUploadPage = location.pathname.startsWith("/upload");
  const isResultsPage = location.pathname === "/upload/results";

  return (
    <>
      {/* Main Navigation Header */}
      <nav
        className={`bg-white dark:bg-[#2C2525] border-b border-gray-200 dark:border-[#5B5353] ${SHADOW.card} z-30 relative`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Brand Section */}
            {/* Logo and Brand Section */}
            <div className="flex items-center space-x-3">
              {/* Brand Logo/Name */}
              <NavLink
                to="/upload"
                className="flex items-center space-x-3 group"
              >
                <img
                  src={ceLogoBlack}
                  alt="CE Logo"
                  className="h-10 dark:hidden"
                />
                <img
                  src={ceLogoWhite}
                  alt="CE Logo"
                  className="h-10 hidden dark:block"
                />
                <div className="hidden sm:block">
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    Campaign Reports
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Performance Analytics
                  </div>
                </div>
              </NavLink>
            </div>

            {/* Right Side Controls */}
            <div className="flex items-center space-x-3">
              {/* Upload Button */}
              <NavLink
                to="/upload"
                className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-[#403838] text-gray-700 dark:text-[#FFF1F3] hover:bg-gray-200 dark:hover:bg-[#5B5353] transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <span className="hidden sm:inline">Upload</span>
              </NavLink>

              <NavLink
                to="/hess-sku-yoy"
                className={({ isActive }) =>
                  `hidden items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors md:flex ${
                    isActive
                      ? "bg-slate-900 text-white dark:bg-[#FFF1F3] dark:text-[#2C2525]"
                      : "bg-gray-100 dark:bg-[#403838] text-gray-700 dark:text-[#FFF1F3] hover:bg-gray-200 dark:hover:bg-[#5B5353]"
                  }`
                }
              >
                <span>Hess YoY</span>
              </NavLink>

              <NavLink
                to="/resers-q1"
                className={({ isActive }) =>
                  `hidden items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors md:flex ${
                    isActive
                      ? "bg-slate-900 text-white dark:bg-[#FFF1F3] dark:text-[#2C2525]"
                      : "bg-gray-100 dark:bg-[#403838] text-gray-700 dark:text-[#FFF1F3] hover:bg-gray-200 dark:hover:bg-[#5B5353]"
                  }`
                }
              >
                <BarChart3 className="h-4 w-4" aria-hidden="true" />
                <span>Reser's Q1</span>
              </NavLink>

              {/* Report History Button (shown on results pages) */}
              {showReportHistory && onOpenReportHistory && (
                <button
                  onClick={onOpenReportHistory}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-[#403838] text-gray-700 dark:text-[#FFF1F3] hover:bg-gray-200 dark:hover:bg-[#5B5353] transition-colors"
                  title="View saved reports"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  <span className="hidden sm:inline">Reports</span>
                  {reportHistoryCount > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full">
                      {reportHistoryCount}
                    </span>
                  )}
                </button>
              )}

              {/* Export Screenshot Button */}
              <button
                type="button"
                onClick={onExportScreenshot}
                disabled={isExporting}
                className={`hidden sm:flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isExporting
                    ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    : `bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white ${SHADOW.cardHover}`
                }`}
                title="Export dashboard as screenshot"
              >
                {isExporting ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span className="hidden lg:inline">Exporting...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="hidden lg:inline">Export</span>
                  </>
                )}
              </button>

              {/* Mobile Export Button */}
              <button
                type="button"
                onClick={onExportScreenshot}
                disabled={isExporting}
                className={`sm:hidden p-2 rounded-lg transition-all ${
                  isExporting
                    ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
                title="Export screenshot"
              >
                {isExporting ? (
                  <svg
                    className="w-5 h-5 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </button>

              {/* View Mode Toggle (Results Page Only)
              {isResultsPage && viewMode && onViewModeChange && (
                <div className="hidden lg:inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 p-1">
                  <button
                    onClick={() => onViewModeChange("original")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      viewMode === "original"
                        ? `bg-gray-50 dark:bg-[#5B5353] text-blue-600 dark:text-blue-400 ${SHADOW.card}`
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    }`}
                    title="Web view - continuous scroll"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <rect
                        x="2"
                        y="3"
                        width="20"
                        height="14"
                        rx="2"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line
                        x1="8"
                        y1="21"
                        x2="16"
                        y2="21"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line
                        x1="12"
                        y1="17"
                        x2="12"
                        y2="21"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="hidden xl:inline">Web</span>
                  </button>
                  <button
                    onClick={() => onViewModeChange("slide")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      viewMode === "slide"
                        ? `bg-gray-50 dark:bg-[#5B5353] text-blue-600 dark:text-blue-400 ${SHADOW.card}`
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    }`}
                    title="Slide view - 16:9 format for presentations"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <rect
                        x="2"
                        y="3"
                        width="20"
                        height="14"
                        rx="2"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7 21h10"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 17v4"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="hidden xl:inline">Slide</span>
                  </button>
                  <button
                    onClick={() => onViewModeChange("compact")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      viewMode === "compact"
                        ? `bg-gray-50 dark:bg-[#5B5353] text-blue-600 dark:text-blue-400 ${SHADOW.card}`
                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    }`}
                    title="Compact view - executive 2-slide format"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <polygon
                        points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="hidden xl:inline">Compact</span>
                  </button>
                </div>
              )}
              */}

              {/* Dark Mode Toggle
              <button
                type="button"
                onClick={onToggleDarkMode}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                title={
                  darkMode ? "Switch to light mode" : "Switch to dark mode"
                }
              >
                {darkMode ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                    />
                  </svg>
                )}
              </button>
              */}
            </div>
          </div>
        </div>
      </nav>

      {/* Error Message Display */}
      {exportError && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Export failed
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {exportError}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={onClearExportError}
                  className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-800/30 transition-colors"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
